import { z } from 'zod';
import { PAYMENT_STATUSES, paymentSummary, type PaymentStatus } from './states/payment';

const uuid = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, 'UUID invalide');
const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date AAAA-MM-JJ');

/**
 * Paiements (CDC §10). Le statut de paiement d'un dossier est CALCULÉ à partir de ses paiements
 * (`paymentSummary`) et reste indépendant du statut du dossier et des interventions.
 *
 *  - Stripe : paiement dans l'application ou par lien (Checkout) ; le statut ne change QUE sur
 *    webhook serveur à signature vérifiée ;
 *  - moyens hors Stripe administrables (espèces, chèque, virement…) : ajoutables, renommables,
 *    activables / désactivables, réordonnables ;
 *  - espèces (et tout moyen réglé « à remettre ») : montant, date, collecteur, état « à remettre »
 *    jusqu'à confirmation par une personne autorisée ; journal complet.
 */
export const PAYMENT_METHOD_KINDS = ['stripe', 'cash', 'check', 'transfer', 'other'] as const;
export type PaymentMethodKind = (typeof PAYMENT_METHOD_KINDS)[number];
export const PAYMENT_METHOD_KIND_LABELS: Record<PaymentMethodKind, string> = {
  stripe: 'Carte bancaire (Stripe)',
  cash: 'Espèces',
  check: 'Chèque',
  transfer: 'Virement',
  other: 'Autre',
};

export interface PaymentMethodRecord {
  id: string;
  version: number;
  updatedAt: string;
  code: string;
  label: string;
  kind: PaymentMethodKind;
  isActive: boolean;
  sortOrder: number;
  /** remis physiquement à l'entreprise (espèces, chèque) : « à remettre » jusqu'à confirmation */
  requiresRemittance: boolean;
  /** n° de chèque, référence de virement… obligatoire */
  requiresReference: boolean;
  /** consignes affichées à l'encaissement (ex. « ordre : P.H SERVICES ») */
  instructions: string | null;
  /** Stripe : clés configurées sur le serveur ; toujours vrai pour les autres moyens */
  configured: boolean;
}

export const paymentMethodInput = z.object({
  label: z.string().trim().min(1, 'Libellé obligatoire').max(80),
  kind: z.enum(PAYMENT_METHOD_KINDS),
  isActive: z.boolean().default(true),
  requiresRemittance: z.boolean().default(false),
  requiresReference: z.boolean().default(false),
  instructions: z.string().trim().max(500).nullish().transform((v) => v || null),
});
export const paymentMethodPatch = paymentMethodInput.omit({ kind: true }).partial().extend({ sortOrder: z.number().int().min(0).max(10_000).optional() });

export interface PaymentRecord {
  id: string;
  version: number;
  updatedAt: string;
  dossierId: string;
  territoryId: string;
  methodId: string;
  /** figés à l'encaissement (le moyen peut être renommé / désactivé ensuite) */
  methodKind: PaymentMethodKind;
  methodLabel: string;
  isDeposit: boolean;
  amountCents: number;
  refundedCents: number;
  status: PaymentStatus;
  /** date de l'encaissement déclarée (espèces, chèque…) */
  receivedOn: string | null;
  reference: string | null;
  comment: string | null;
  collectedById: string;
  collectedByName: string;
  confirmedById: string | null;
  confirmedByName: string | null;
  confirmedAt: string | null;
  statusReasonId: string | null;
  statusComment: string | null;
  /** Stripe : lien de paiement à ouvrir / partager, sa date d'expiration, ou l'erreur de création */
  checkoutUrl: string | null;
  checkoutExpiresAt: string | null;
  checkoutError: string | null;
  /** changement de moyen de paiement : paiement qui remplace celui-ci / qu'il remplace */
  replacedByPaymentId: string | null;
  replacesPaymentId: string | null;
  createdAt: string;
}

export const paymentCreateInput = z.object({
  id: uuid,
  dossierId: uuid,
  methodId: uuid,
  amountCents: z.number().int().min(1, 'Montant invalide').max(100_000_000),
  isDeposit: z.boolean().default(false),
  receivedOn: date.nullish().transform((v) => v ?? null),
  reference: z.string().trim().max(100).nullish().transform((v) => v || null),
  comment: z.string().trim().max(1000).nullish().transform((v) => v || null),
});
export type PaymentCreateInput = z.input<typeof paymentCreateInput>;

export const paymentTransitionInput = z.object({
  from: z.enum(PAYMENT_STATUSES),
  to: z.enum(PAYMENT_STATUSES),
  reasonId: uuid.nullish().transform((v) => v ?? null),
  comment: z.string().trim().max(1000).nullish().transform((v) => v || null),
  /** remboursement hors Stripe : montant remboursé (cumulé) */
  refundedCents: z.number().int().min(0).nullish(),
});
export type PaymentTransitionInput = z.input<typeof paymentTransitionInput>;

/**
 * Changement de moyen de paiement (administrateur) : un paiement pas encore encaissé est remplacé par un paiement du
 * même montant avec un autre moyen. L'ancien est annulé (motif « Changement de moyen de paiement »), jamais supprimé.
 */
export const PAYMENT_CHANGEABLE_STATUSES: PaymentStatus[] = ['pending', 'failed', 'to_remit'];
export const paymentChangeMethodInput = z.object({
  newPaymentId: uuid,
  methodId: uuid,
  receivedOn: date.nullish().transform((v) => v ?? null),
  reference: z.string().trim().max(100).nullish().transform((v) => v || null),
  comment: z.string().trim().max(1000).nullish().transform((v) => v || null),
});
export type PaymentChangeMethodInput = z.input<typeof paymentChangeMethodInput>;

export const paymentRefundInput = z.object({
  amountCents: z.number().int().min(1),
  comment: z.string().trim().min(3, 'Motif du remboursement obligatoire').max(1000),
});

/** Statut à la création : « à remettre » pour un moyen remis physiquement, sinon en attente. */
export function initialPaymentStatus(method: Pick<PaymentMethodRecord, 'kind' | 'requiresRemittance'>): PaymentStatus {
  if (method.kind === 'stripe') return 'pending';
  return method.requiresRemittance ? 'to_remit' : 'pending';
}

/** Paiements qui « comptent » dans le plafond : tout sauf annulés / échoués (net des remboursements). */
export function committedCents(payments: Pick<PaymentRecord, 'status' | 'amountCents' | 'refundedCents'>[]): number {
  return payments.filter((p) => !['canceled', 'failed', 'refunded'].includes(p.status)).reduce((s, p) => s + p.amountCents - p.refundedCents, 0);
}

/** Montant encore encaissable sur un dossier : jamais au-delà du total TTC signé. */
export function remainingToCollect(totalCents: number, payments: Pick<PaymentRecord, 'status' | 'amountCents' | 'refundedCents'>[]): number {
  return Math.max(totalCents - committedCents(payments), 0);
}

/** Récapitulatif affiché sur le dossier (À payer, Partiellement payé, Payé, En cours, Remboursé). */
export function dossierPaymentSummary(totalCents: number, payments: Pick<PaymentRecord, 'status' | 'amountCents' | 'refundedCents'>[]) {
  return {
    totalCents,
    ...paymentSummary(
      totalCents,
      payments.map((p) => ({ status: p.status, amountCents: p.amountCents, refundedCents: p.refundedCents })),
    ),
    remainingCents: remainingToCollect(totalCents, payments),
  };
}

/** Moyens par défaut d'une nouvelle entreprise (administrables ensuite). */
export const DEFAULT_PAYMENT_METHODS: { code: string; label: string; kind: PaymentMethodKind; requiresRemittance: boolean; requiresReference: boolean; instructions?: string }[] = [
  { code: 'carte', label: 'Carte bancaire', kind: 'stripe', requiresRemittance: false, requiresReference: false },
  { code: 'especes', label: 'Espèces', kind: 'cash', requiresRemittance: true, requiresReference: false },
  { code: 'cheque', label: 'Chèque', kind: 'check', requiresRemittance: true, requiresReference: true },
  { code: 'virement', label: 'Virement', kind: 'transfer', requiresRemittance: false, requiresReference: false },
];

/** Saisie « 1 234,56 » → 123456 centimes ; null si vide ou invalide. */
export function parseEurosToCents(v: string): number | null {
  const n = Number(v.replace(/[\s €]/g, '').replace(',', '.'));
  return v.trim() === '' || !Number.isFinite(n) || n < 0 ? null : Math.round(n * 100);
}

/** Libellés affichés du statut de paiement d'un dossier, avec leur ton. */
export const PAYMENT_SUMMARY_TONES = { to_pay: 'warning', partially_paid: 'info', paid: 'success', in_progress: 'progress', refunded: 'neutral' } as const;
